import { useLazyQuery } from "@apollo/client";
import { useFocusEffect } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import { Card, Page, Stack, Button } from "@towersystems/roam-common-native";
import { TerminalClosure } from "@towersystems/roam-common/lib/generated-types";
import { useCallback, useEffect } from "react";
import { Spacings, View } from "react-native-ui-lib";
import { ShiftRootParamList } from "../../../../types";
import { moneyToFormattedString } from "../../../../utilities/money";
import { QUERY_TERMINAL_SHIFT } from "../../../../utilities/terminal-shift/constants";
import { CashMovementList } from "../../components/CashMovementList";
import { ShiftTotalsSummary } from "../../components/ShiftTotalsSummary";
import { useEndOfShiftContextProvider } from "../../utils/shift/hooks";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";
import { useTranslation } from "../../../../utilities/i18n";
import { FlatList } from "react-native";

export interface OpenShiftScreenProps
  extends StackScreenProps<ShiftRootParamList, "OpenShiftScreen"> {}

export const OpenShiftScreen = ({ navigation }: OpenShiftScreenProps) => {
  const { t } = useTranslation("end_of_shift");

  const { shiftTerminal } = useEndOfShiftContextProvider();

  const [q, { loading, data, error }] = useLazyQuery<{
    terminalClosure: TerminalClosure;
  }>(QUERY_TERMINAL_SHIFT);

  useFocusEffect(
    useCallback(() => {
      q({
        variables: {
          id: shiftTerminal.openTerminalClosureId,
        },
      }).catch((err) => console.log(err));
    }, [shiftTerminal.openTerminalClosureId])
  );

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => null,
    });
  }, []);

  const closeShiftMarkup = (
    <View marginT-20>
      <Button
        label={t("button.close_shift")}
        isDestructive
        onPress={() =>
          navigation.navigate("CloseShiftRootScreen", {
            screen: "CloseCountCashScreen",
          })
        }
      ></Button>
    </View>
  );

  if (loading) {
    return <ResourcePageSkeleton />;
  }

  if (error || !data) return <Page title="Error..."></Page>;

  const item = data.terminalClosure;
  const title = moneyToFormattedString(item.estimatedCashBalance);



  return (
    <Page
      title={title}
      secondaryTitle={t("expected_balance")}
      footer={closeShiftMarkup}
    >
      <Stack vertical>
        <View>
          <FlatList
            horizontal
            scrollEnabled={true}
            ItemSeparatorComponent={() => (
              <View
                style={{
                  height: "100%",
                  width: Spacings.s4 * 2,
                }}
              ></View>
            )}
            keyExtractor={(item) => item.id.toString()}
            data={[
              {
                id: 1,
                label: t("action.add_cash.label"),
                isAction: true,
                onPress: () => navigation.navigate("AddCashToOpenShiftScreen"),
              },
              {
                id: 2,
                isAction: true,
                label: t("action.remove_cash.label"),
                onPress: () =>
                  navigation.navigate("RemoveCashFromOpenShiftScreen"),
              },
            ]}
            renderItem={({ item }: any) => {
              return <Button style={{ width: 230, height: 120 }} {...item} />;
            }}
          ></FlatList>
        </View>
        <Card title={t("card.summary.title")} filled>
          <ShiftTotalsSummary item={item.saleSummary} />
        </Card>
        <Card title={t("card.movements.title")} filled>
          <CashMovementList cashMovements={item.cashMovements} />
        </Card>
      </Stack>
    </Page>
  );
};
